import React, { useRef } from 'react'
import { useFolders } from '../../contexts/FoldersContext'
import { ChevronLeft, ChevronRight, Pin, Lock, Folder, FileText } from 'lucide-react'
import FolderTab from './FolderTab'

/**
 * Horizontal scrollable folder tab bar component.
 * 
 * Displays all user folders in a horizontal scrollable layout with:
 * - "All Notes" tab for root notes
 * - Default folders (Pinned, Sensitive) always first
 * - Custom folders in creation order
 * - Smooth horizontal scrolling with navigation arrows
 */

const FolderTabBar = ({ 
  allNotesCount = 0,
  onFolderSelect,
  onShowAllNotes 
}) => {
  const { 
    folders, 
    currentFolder, 
    isShowingAllNotes, 
    loading 
  } = useFolders()
  
  const scrollContainerRef = useRef(null)

  /**
   * Scroll the tab container left or right
   */
  const scrollTabs = (direction) => {
    const container = scrollContainerRef.current
    if (!container) return

    const scrollAmount = 200
    const newScrollLeft = direction === 'left' 
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    })
  }

  /**
   * Handle folder tab click
   */
  const handleFolderClick = (folder) => {
    // In cloud mode, sensitive folders can be accessed without authentication
    // Authentication will be required when viewing individual sensitive notes
    console.log('🔐 FolderTabBar: Folder clicked:', folder.name, 'type:', folder.folderType)
    
    // Proceed with normal folder selection
    onFolderSelect?.(folder)
  }



  /**
   * Handle "All Notes" tab click
   */
  const handleAllNotesClick = () => {
    onShowAllNotes?.()
  }

  if (loading && folders.length === 0) {
    return (
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3">
          <div className="animate-pulse flex space-x-4">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-28"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 w-full max-w-full overflow-x-hidden">
      <div className="relative w-full max-w-full overflow-x-hidden">
        {/* Left scroll arrow - Hidden on mobile */}
        <button
          onClick={() => scrollTabs('left')}
          className="absolute left-0 top-0 bottom-0 z-10 flex items-center justify-center w-8 bg-gradient-to-r from-white to-transparent dark:from-gray-900 dark:to-transparent hover:from-gray-50 dark:hover:from-gray-800 transition-colors hidden md:flex"
          aria-label="Scroll left"
        >
          <ChevronLeft className="h-4 w-4 text-gray-500" />
        </button>

        {/* Scrollable tabs container */}
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide py-2 gap-1 px-2 md:px-8 w-full max-w-full"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitScrollbar: { display: 'none' },
            WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
            scrollBehavior: 'smooth' // Smooth scrolling for programmatic scrolls
          }}
        >
          {/* All Notes Tab - count temporarily disabled */}
          <FolderTab
            isActive={isShowingAllNotes}
            onClick={handleAllNotesClick}
            icon={getAllNotesIcon(isShowingAllNotes)}
            name="All Notes"
            isDefault={true}
            isAllNotesTab={true}
          />

          {/* Folder Tabs - counts temporarily disabled */}
          {folders.map(folder => {
            // console.log('🔍 FolderTabBar: Rendering folder tab:', folder.name, 'type:', folder.folderType)
            return (
              <FolderTab
                key={folder.folderId}
                isActive={currentFolder?.folderId === folder.folderId}
                onClick={() => {
                  // console.log('🔍 FolderTabBar: Tab clicked:', folder.name, 'type:', folder.folderType)
                  handleFolderClick(folder)
                }}
                icon={getFolderIcon(folder.folderType, folder.color, currentFolder?.folderId === folder.folderId)}
                name={folder.name}
                color={folder.color}
                isDefault={folder.isDefault}
                folderType={folder.folderType}
              />
            )
          })}
        </div>

        {/* Right scroll arrow - Hidden on mobile */}
        <button
          onClick={() => scrollTabs('right')}
          className="absolute right-0 top-0 bottom-0 z-10 flex items-center justify-center w-8 bg-gradient-to-l from-white to-transparent dark:from-gray-900 dark:to-transparent hover:from-gray-50 dark:hover:from-gray-800 transition-colors hidden md:flex"
          aria-label="Scroll right"
        >
          <ChevronRight className="h-4 w-4 text-gray-500" />
        </button>
      </div>


    </div>
  )
}

/**
 * Get the appropriate SVG icon component for a folder type
 */
const getFolderIcon = (folderType, color = '#6B7280', isActive = false) => {
  // Use predefined colors for default folders or passed color for custom folders
  let iconColor = color
  
  if (folderType === 'PINNED') {
    iconColor = isActive ? '#FFD700' : '#B8860B' // Gold/DarkGoldenRod
  } else if (folderType === 'SENSITIVE') {
    iconColor = isActive ? '#DC2626' : '#991B1B' // Red/DarkRed
  }

  const iconProps = {
    size: 16,
    color: iconColor,
    className: 'flex-shrink-0'
  }

  switch (folderType) {
    case 'PINNED':
      return <Pin {...iconProps} />
    case 'SENSITIVE':
      return <Lock {...iconProps} />
    case 'CUSTOM':
    default:
      return <Folder {...iconProps} />
  }
}

/**
 * Get the "All Notes" icon
 */
const getAllNotesIcon = (isActive = false) => {
  const color = isActive ? '#2563EB' : '#6B7280' // Blue when active, gray when inactive
  return <FileText size={16} color={color} className="flex-shrink-0" />
}

export default FolderTabBar 